import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
  Dropdown,
  Modal,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import {
  PrinterOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SaveOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReportDefinition, ReportQueryResult } from '@/reports/types';
import reportsService from '@/services/reports.service';
import { downloadCsv, downloadXlsx } from '@/reports/utils/exportTable';
import { printReportTable } from '@/reports/utils/printReport';
import { formatMoney, formatDate } from '@/utils/format';
import { ProjectSelector } from '@/components/project/ProjectSelector';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ASYNC_EXPORT_THRESHOLD = 5000;

type Props = {
  definition: ReportDefinition;
};

function formatCell(value: unknown, format?: string): React.ReactNode {
  if (value == null || value === '') return '—';
  if (format === 'money') {
    const n = Number(value);
    return Number.isFinite(n) ? formatMoney(n) : String(value);
  }
  if (format === 'percent') {
    const n = Number(value);
    return Number.isFinite(n)
      ? `${n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 })}%`
      : String(value);
  }
  if (format === 'date') {
    return formatDate(String(value));
  }
  if (format === 'number') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : String(value);
  }
  const text = String(value).trim();
  // Never show raw UUID as a report label (wrong field mapping / missing join)
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)) {
    return '—';
  }
  return text;
}

const NON_ADDITIVE_COLUMN = /(pct|percent|unit|rate|avg|price|share)/i;

/**
 * List-style endpoints return every row with no server totals; sum their additive money columns
 * as the grand total. Paginated responses are left to the backend.
 */
function withClientGrandTotals(
  result: ReportQueryResult,
  columns: NonNullable<ReportDefinition['columnSchema']>
): ReportQueryResult {
  if (result.totals || result.grandTotals || result.rows.length < result.total) return result;
  const keys = columns
    .filter((c) => c.format === 'money' && !NON_ADDITIVE_COLUMN.test(c.dataIndex))
    .map((c) => c.dataIndex);
  if (!keys.length || !result.rows.length) return result;
  const grandTotals: Record<string, number> = {};
  for (const key of keys) {
    grandTotals[key] =
      Math.round(result.rows.reduce((s, r) => s + (Number(r[key]) || 0), 0) * 100) / 100;
  }
  return { ...result, grandTotals };
}

function summaryLabel(hasGrandTotals: boolean, kind: 'page' | 'grand'): string {
  if (kind === 'grand') return 'Grand Total';
  return hasGrandTotals ? 'Page Total' : 'Totals';
}

export const ReportRunner: React.FC<Props> = ({ definition }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const page = Number(searchParams.get('page') || 1);
  const pageSize = Number(searchParams.get('pageSize') || 50);
  const sortBy = searchParams.get('sortBy') || definition.sortDefaults?.sortBy || '';
  const sortDir = (searchParams.get('sortDir') || definition.sortDefaults?.sortDir || 'DESC') as
    | 'ASC'
    | 'DESC';
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const ageBucket = searchParams.get('age_bucket') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const view = searchParams.get('view') || '';
  const tolerance = searchParams.get('tolerance') || '';
  const projectId = searchParams.get('project_id') || '';

  const columnsSchema = useMemo(() => {
    const byView = definition.columnSchemaByView;
    if (view && byView?.[view]?.length) return byView[view];
    return definition.columnSchema || [];
  }, [definition.columnSchema, definition.columnSchemaByView, view]);

  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleName, setScheduleName] = useState('');
  const [scheduleCron, setScheduleCron] = useState('0 8 * * 1');

  useEffect(() => {
    setHiddenCols(new Set());
  }, [definition.id, view]);

  const visibleColumns = useMemo(
    () => columnsSchema.filter((c) => !hiddenCols.has(c.key)),
    [columnsSchema, hiddenCols]
  );

  const queryParams = useMemo(() => {
    const p: Record<string, unknown> = {
      page,
      pageSize,
      sortBy: sortBy || undefined,
      sortDir,
    };
    const put = (key: string, value: string | undefined | null) => {
      const v = String(value ?? '').trim();
      if (v) p[key] = v;
    };
    put('search', search);
    put('status', status);
    put('age_bucket', ageBucket);
    put('date_from', dateFrom);
    put('date_to', dateTo);
    put('view', view);
    put('tolerance', tolerance);
    put('project_id', projectId);

    // Pass every filterSchema key from the URL (not only the hardcoded whitelist above).
    // dateRange maps to date_from/date_to; skip duplicates already set.
    for (const f of definition.filterSchema || []) {
      if (f.key === 'dateRange') continue;
      if (p[f.key] != null) continue;
      put(f.key, searchParams.get(f.key));
    }
    return p;
  }, [
    page,
    pageSize,
    sortBy,
    sortDir,
    search,
    status,
    ageBucket,
    dateFrom,
    dateTo,
    view,
    tolerance,
    projectId,
    definition.filterSchema,
    searchParams,
  ]);

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['report-runner', definition.id, queryParams],
    queryFn: async () => {
      if (!definition.dataSource) {
        return { rows: [], total: 0, page: 1, pageSize: 50 };
      }
      const result = withClientGrandTotals(
        await reportsService.queryReport(definition.dataSource, queryParams),
        columnsSchema
      );
      void reportsService.logRun({
        reportId: definition.id,
        action: 'view',
        filters: queryParams,
        rowCount: result.total,
      });
      return result;
    },
    enabled: definition.kind === 'runner' && Boolean(definition.dataSource),
  });

  const savedViewsQuery = useQuery({
    queryKey: ['report-saved-views', definition.id],
    queryFn: () => reportsService.listSavedViews(definition.id),
    enabled: Boolean(definition.capabilities.savedViews),
  });

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '') next.delete(k);
        else next.set(k, v);
      });
      if (!('page' in patch)) next.set('page', '1');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const tableColumns: ColumnsType<Record<string, unknown>> = visibleColumns.map((c) => ({
    title: c.title,
    dataIndex: c.dataIndex,
    key: c.key,
    width: c.width,
    align: c.align,
    sorter: Boolean(c.sortable),
    sortOrder: sortBy === c.dataIndex ? (sortDir === 'ASC' ? 'ascend' : 'descend') : undefined,
    render: (value: unknown, row: Record<string, unknown>) => {
      const content = formatCell(value, c.format);
      if (c.drillPath && row.id) {
        const to = c.drillPath.replace(':id', String(row.id));
        return <Link to={to}>{content}</Link>;
      }
      return content;
    },
  }));

  const onTableChange = (
    pagination: TablePaginationConfig,
    _f: unknown,
    sorter: SorterResult<Record<string, unknown>> | SorterResult<Record<string, unknown>>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const next: Record<string, string | null> = {
      page: String(pagination.current || 1),
      pageSize: String(pagination.pageSize || pageSize),
    };
    if (s?.field) {
      next.sortBy = String(s.field);
      next.sortDir = s.order === 'ascend' ? 'ASC' : 'DESC';
    }
    patchParams(next);
  };

  const exportRows = async (format: 'csv' | 'xlsx') => {
    if (!definition.dataSource) return;
    try {
      if ((data?.total || 0) > ASYNC_EXPORT_THRESHOLD) {
        await reportsService.enqueueExport({
          reportId: definition.id,
          format,
          filters: queryParams,
          sort: { sortBy, sortDir },
        });
        message.info('Large export queued. Check export jobs when ready.');
        return;
      }
      const all = await reportsService.queryReport(definition.dataSource, {
        ...queryParams,
        page: 1,
        pageSize: Math.min(Math.max(data?.total || pageSize, pageSize), 5000),
      });
      const name = `${definition.id}-${dayjs().format('YYYYMMDD')}`;
      if (format === 'csv') downloadCsv(name, visibleColumns, all.rows);
      else await downloadXlsx(name, visibleColumns, all.rows);
      await reportsService.logRun({
        reportId: definition.id,
        action: 'export',
        filters: queryParams,
        rowCount: all.rows.length,
        exportFormat: format,
      });
      message.success(`${format.toUpperCase()} downloaded`);
    } catch (e: any) {
      message.error(e?.message || 'Export failed');
    }
  };

  const onPrint = async () => {
    if (!definition.dataSource) return;
    const isCustomerSummary = view === 'customer_summary' || view === 'by_customer';
    const totalLines = (values: Record<string, number> | undefined, label: string) =>
      visibleColumns
        .filter((c) => c.format === 'money' && values?.[c.dataIndex] != null)
        .map((c) => ({ label: `${label} — ${c.title}`, value: formatMoney(Number(values![c.dataIndex])) }));
    try {
      const all = await reportsService.queryReport(definition.dataSource, {
        ...queryParams,
        page: 1,
        pageSize: Math.min(Math.max(data?.total || pageSize, pageSize), 5000),
      });
      const printRows = withClientGrandTotals(all, columnsSchema);
      // Customer summary has 6 money/label columns that overflow A4 portrait (Balance clipped).
      // Detail register is wider still — both use Wave E landscape report profile.
      const useLandscape =
        definition.id === 'sales.invoice_register' ||
        visibleColumns.length >= 6 ||
        visibleColumns.reduce((sum, c) => sum + (c.width || 120), 0) > 720;
      printReportTable({
        definition,
        columns: visibleColumns,
        rows: printRows.rows || [],
        landscape: useLandscape,
        viewLabel: isCustomerSummary ? 'Customer summary' : 'Invoice detail',
        metaLines: [
          { label: 'Generated', value: dayjs().format('YYYY-MM-DD HH:mm') },
          { label: 'View', value: isCustomerSummary ? 'Customer summary' : 'Invoice detail' },
          ...(status ? [{ label: 'Status', value: status }] : []),
          ...(search ? [{ label: 'Search', value: search }] : []),
          ...(dateFrom || dateTo
            ? [{ label: 'Period', value: `${dateFrom || '…'} to ${dateTo || '…'}` }]
            : []),
          ...totalLines(printRows.totals, printRows.grandTotals ? 'Page Total' : 'Total'),
          ...totalLines(printRows.grandTotals, 'Grand Total'),
        ],
      });
      void reportsService.logRun({
        reportId: definition.id,
        action: 'print',
        filters: queryParams,
        rowCount: printRows.rows?.length,
      });
    } catch (e: any) {
      message.error(e?.message || 'Print failed');
    }
  };

  const saveCurrentView = async () => {
    const name = window.prompt('Saved view name');
    if (!name?.trim()) return;
    try {
      await reportsService.createSavedView({
        report_id: definition.id,
        name: name.trim(),
        filters_json: queryParams,
        columns_json: { hidden: Array.from(hiddenCols) },
        sort_json: { sortBy, sortDir },
      });
      message.success('View saved');
      void queryClient.invalidateQueries({ queryKey: ['report-saved-views', definition.id] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Could not save view');
    }
  };

  const applySavedView = (viewId: string) => {
    const view = savedViewsQuery.data?.find((v) => v.id === viewId);
    if (!view) return;
    const f = view.filters_json || {};
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (v != null && v !== '') next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
    const hidden = (view.columns_json as any)?.hidden as string[] | undefined;
    if (hidden) setHiddenCols(new Set(hidden));
  };

  const createSchedule = async () => {
    if (!scheduleName.trim()) {
      message.warning('Name required');
      return;
    }
    try {
      await reportsService.createSchedule({
        report_id: definition.id,
        name: scheduleName.trim(),
        cron_expression: scheduleCron,
        export_format: 'xlsx',
        filters_json: queryParams,
      });
      message.success('Schedule created (email delivery via Notification Platform in hardening)');
      setScheduleOpen(false);
      setScheduleName('');
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Schedule failed');
    }
  };

  useEffect(() => {
    if (definition.kind !== 'runner') return;
  }, [definition.kind]);

  if (definition.kind !== 'runner') {
    return (
      <Card>
        <Text>
          This report opens at <Link to={definition.route}>{definition.route}</Link>
        </Text>
      </Card>
    );
  }

  return (
    <div style={{ padding: 8 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {definition.title}
          </Title>
          <Text type="secondary">{definition.description}</Text>
        </div>
        <Space wrap>
          {definition.capabilities.savedViews && (
            <>
              <Select
                placeholder="Saved views"
                style={{ minWidth: 160 }}
                allowClear
                options={(savedViewsQuery.data || []).map((v) => ({ value: v.id, label: v.name }))}
                onChange={(id) => id && applySavedView(id)}
              />
              <Button icon={<SaveOutlined />} onClick={saveCurrentView}>
                Save view
              </Button>
            </>
          )}
          {definition.capabilities.schedule && (
            <Button icon={<ClockCircleOutlined />} onClick={() => setScheduleOpen(true)}>
              Schedule
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Refresh
          </Button>
          {definition.capabilities.print && (
            <Button icon={<PrinterOutlined />} onClick={onPrint}>
              Print
            </Button>
          )}
          {definition.capabilities.exportCsv && (
            <Button icon={<FileTextOutlined />} onClick={() => exportRows('csv')}>
              CSV
            </Button>
          )}
          {definition.capabilities.exportXlsx && (
            <Button icon={<FileExcelOutlined />} onClick={() => exportRows('xlsx')}>
              Excel
            </Button>
          )}
          <Dropdown
            menu={{
              items: columnsSchema.map((c) => ({
                key: c.key,
                label: (
                  <label onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!hiddenCols.has(c.key)}
                      onChange={(e) => {
                        setHiddenCols((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.delete(c.key);
                          else next.add(c.key);
                          return next;
                        });
                      }}
                    />{' '}
                    {c.title}
                  </label>
                ),
              })),
            }}
          >
            <Button>Columns</Button>
          </Dropdown>
        </Space>
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]}>
          {(definition.filterSchema || []).map((f) => (
            <Col key={f.key} xs={24} sm={12} md={8} lg={6}>
              {f.type === 'project' && (
                <ProjectSelector
                  value={
                    (f.key === 'project_id' ? projectId : searchParams.get(f.key) || '') || undefined
                  }
                  allowClear={f.allowClear !== false}
                  placeholder={f.placeholder || f.label || 'Search project by code or name'}
                  onChange={(pid) => patchParams({ [f.key]: pid || null })}
                />
              )}
              {f.type === 'text' && (
                <Input.Search
                  allowClear
                  placeholder={f.placeholder || f.label}
                  defaultValue={
                    f.key === 'search'
                      ? search
                      : searchParams.get(f.key) || undefined
                  }
                  onSearch={(v) =>
                    patchParams({ [f.key === 'search' ? 'search' : f.key]: v || null })
                  }
                />
              )}
              {f.type === 'select' && (
                <Select
                  allowClear
                  placeholder={f.label}
                  style={{ width: '100%' }}
                  value={
                    f.key === 'status'
                      ? status || undefined
                      : f.key === 'age_bucket'
                        ? ageBucket || undefined
                        : f.key === 'view'
                          ? view || undefined
                          : searchParams.get(f.key) || undefined
                  }
                  options={f.options}
                  onChange={(v) => {
                    if (f.key === 'view') {
                      const nextView = (v as string) || null;
                      const patch: Record<string, string | null> = { view: nextView };
                      if (nextView === 'customer_summary' || nextView === 'by_customer') {
                        patch.sortBy = 'base_total_amount';
                        patch.sortDir = 'DESC';
                      } else {
                        patch.sortBy = definition.sortDefaults?.sortBy || 'invoice_date';
                        patch.sortDir = definition.sortDefaults?.sortDir || 'DESC';
                      }
                      patchParams(patch);
                      return;
                    }
                    patchParams({ [f.key]: (v as string) || null });
                  }}
                />
              )}
              {f.type === 'number' && (
                <Input
                  allowClear
                  type="number"
                  placeholder={f.placeholder || f.label}
                  defaultValue={f.key === 'tolerance' ? tolerance || undefined : searchParams.get(f.key) || undefined}
                  onBlur={(e) => {
                    const v = e.target.value?.trim();
                    patchParams({ [f.key]: v || null });
                  }}
                  onPressEnter={(e) => {
                    const v = (e.target as HTMLInputElement).value?.trim();
                    patchParams({ [f.key]: v || null });
                  }}
                />
              )}
              {f.type === 'dateRange' && (
                <RangePicker
                  style={{ width: '100%' }}
                  value={
                    dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null
                  }
                  onChange={(vals) =>
                    patchParams({
                      date_from: vals?.[0] ? vals[0].format('YYYY-MM-DD') : null,
                      date_to: vals?.[1] ? vals[1].format('YYYY-MM-DD') : null,
                    })
                  }
                />
              )}
            </Col>
          ))}
        </Row>
      </Card>

      {error ? (
        <Card>
          <Text type="danger">{(error as Error).message || 'Failed to load report'}</Text>
        </Card>
      ) : (
        <Table
          rowKey={(r) => String(r.id ?? r.document_number ?? Math.random())}
          loading={isLoading || isFetching}
          columns={tableColumns}
          dataSource={data?.rows || []}
          scroll={{ x: Math.max(800, visibleColumns.reduce((s, c) => s + (c.width || 120), 0)) }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            showTotal: (t) => `${t} rows`,
          }}
          onChange={onTableChange}
          summary={
            data?.totals || data?.grandTotals
              ? () => {
                  const hasGrand = Boolean(data.grandTotals);
                  const renderRow = (
                    label: string,
                    values: Record<string, number> | undefined,
                    keyPrefix: string
                  ) => (
                    <Table.Summary.Row key={keyPrefix}>
                      <Table.Summary.Cell index={0}>{label}</Table.Summary.Cell>
                      {visibleColumns.slice(1).map((c, i) => (
                        <Table.Summary.Cell key={`${keyPrefix}-${c.key}`} index={i + 1} align={c.align}>
                          {values?.[c.dataIndex] != null
                            ? formatCell(values[c.dataIndex], c.format)
                            : ''}
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  );
                  return (
                    <>
                      {data.totals
                        ? renderRow(summaryLabel(hasGrand, 'page'), data.totals, 'page')
                        : null}
                      {data.grandTotals
                        ? renderRow(summaryLabel(hasGrand, 'grand'), data.grandTotals, 'grand')
                        : null}
                    </>
                  );
                }
              : undefined
          }
        />
      )}

      <Modal
        title="Schedule report"
        open={scheduleOpen}
        onOk={createSchedule}
        onCancel={() => setScheduleOpen(false)}
        okText="Create schedule"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Schedule name"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
          />
          <Input
            placeholder="Cron (e.g. 0 8 * * 1)"
            value={scheduleCron}
            onChange={(e) => setScheduleCron(e.target.value)}
          />
          <Text type="secondary">
            Schedules are stored now; email delivery hooks into the Notification Platform in Phase 5/6.
          </Text>
        </Space>
      </Modal>
    </div>
  );
};

export default ReportRunner;
